'use client';

import type { PressEventsType } from '@osmosis/backend/orm-db';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { H1 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { text3, text4, textBlue } from '@osmosis/tailwind-ui/styles/text';

import dayjs from 'dayjs';
import React from 'react';
import { VideoPlayerProvider } from '../../../context/VideoPlayerProvider';
import { FormattedMessage, useIntl } from 'react-intl';
import { PROD_MEDIA_SERVER } from '@osmosis/constants';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import Link from 'next/link';
import { EVENTS_URL, EVENT_URL } from '../../../constants/urls';
import Image from 'next/image';
import { faClosedCaptioning } from '@fortawesome/pro-regular-svg-icons/faClosedCaptioning';
import { faVideoCamera } from '@fortawesome/pro-regular-svg-icons/faVideoCamera';

import LearnBlock from '../../../components/learn/blocks/LearnBlock';
import { titleToUrl } from '../../_lib/shared/events';
import LockoutWrapper from './LockoutWrapper';
import { VideoEnum } from '../../../types/video-player';

type Props = {
  event: PressEventsType;
  events: PressEventsType[];
};

const maxWidth = `max-w-[800px] m-auto`;
const containerClasses = `flex flex-col justify-center ${maxWidth} gap-8`;

/**
 * Event page.
 */
const EventPage = (props: Props) => {
  const { event, events } = props;
  const intl = useIntl();

  // testing out some different lockout times for logged out users viewing events videos
  const watchSeconds = 120; // start with 4 minutes

  return (
    <Box cn="sm:p-5 p-2 md:p-8 flex flex-col gap-8">
      <Box cn={containerClasses}>
        <Box>
          <Link
            href={EVENTS_URL}
            className={`flex items-center gap-2 ${textBlue}`}
          >
            <Icon icon={faChevronLeft} className="text-xs" />
            <FormattedMessage id="common.back" defaultMessage="Back" />
          </Link>
        </Box>
        <Box cn="flex flex-col gap-2">
          <H1 cn="text-center sm:text-2xl text-xl">{event.data.title}</H1>
          <P cn={`${text4} text-center`}>
            {dayjs(event.Date).format('MMMM D, YYYY')}
          </P>

          <Box cn="flex flex-col gap-8">
            <Box cn="flex justify-center">
              <P cn={`${text4}`}>
                <FormattedMessage
                  id="event-page.watch_on_demand"
                  defaultMessage="Watch on Demand"
                />
              </P>
            </Box>

            <P>{event.data.description}</P>
          </Box>
        </Box>
      </Box>

      {!event.data.video_url && event.data.image && (
        <Box>
          <Box cn={maxWidth}>
            <Box cn="w-full relative p-[28.25%]">
              <Image
                src={`${PROD_MEDIA_SERVER}${event.data.image}`}
                fill={true}
                alt={`Event thumbnail for the ${event.data.title} webinar`}
                className="object-contain"
                // Make sure to adjust these sizes if the image sizes are changed to optimize image
                // performance
                sizes="(max-width: 800px) 100vw, 800px"
                data-testid="event-image"
                fetchPriority="high"
              />
            </Box>
          </Box>
        </Box>
      )}

      {event.data.video_url && (
        <Box cn="relative">
          <Box cn={containerClasses}>
            <Box cn="relative">
              <Box>
                <VideoPlayerProvider type={VideoEnum.YOUTUBE}>
                  <LockoutWrapper
                    poster={`${PROD_MEDIA_SERVER}${event.data.image}`}
                    url={event.data.video_url}
                    watchSeconds={watchSeconds}
                  />
                </VideoPlayerProvider>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Box cn={`${maxWidth} flex flex-col gap-8`}>
        {!!event.data.transcript && (
          <LearnBlock
            icon={faClosedCaptioning}
            title={intl.formatMessage({
              id: 'common.transcript',
              defaultMessage: 'Transcript',
            })}
            content={<Box>{event.data.transcript}</Box>}
          />
        )}

        <LearnBlock
          icon={faVideoCamera}
          title={intl.formatMessage({
            id: 'event-page.event_recordings',
            defaultMessage: 'Event recordings',
          })}
          content={
            <Box cn="flex overflow-x-auto gap-8 pb-2 max-w-[calc(100vw-2rem)] md:max-w-full">
              {events.slice(0, 5).map((event) => (
                <Link
                  className="w-1/3 flex-none"
                  href={`${EVENT_URL}/${titleToUrl(event.data.title)}`}
                  key={event.eventId}
                >
                  <Box cn="flex flex-col gap-1 w-full p-2 rounded-lg hover:bg-slate-100 dark:hover-bg-slate-800 cursor-pointer">
                    <Box cn="relative w-full p-[28.25%]">
                      <Image
                        src={`${PROD_MEDIA_SERVER}${event.data.image}`}
                        fill={true}
                        alt=""
                        loading="lazy"
                      />
                    </Box>
                    <div
                      className="line-clamp-2 sm:text-sm"
                      style={{ fontWeight: 550 }}
                    >
                      {event.data.title}
                    </div>
                    <P cn={`sm:text-xs ${text3}`}>
                      {dayjs(event.Date).format('MMMM D, YYYY')}
                    </P>
                  </Box>
                </Link>
              ))}
            </Box>
          }
        />
      </Box>
    </Box>
  );
};

export default EventPage;
